import { message } from 'antd';
import enUS from 'antd/es/locale/en_US';
import koKR from 'antd/es/locale/ko_KR';
import zhCN from 'antd/es/locale/zh_CN';
import idIN from 'antd/es/locale/id_ID';
import thTH from 'antd/es/locale/th_TH';
import zhTW from 'antd/es/locale/zh_TW';
import esES from 'antd/es/locale/es_ES';
import ptPT from 'antd/es/locale/pt_PT';
import { AxiosError } from 'axios';
import i18n, { TFunctionResult } from 'i18next';
import _get from 'lodash/get';
import React from 'react';
import PackageJson from '../../package.json';
import _ from 'lodash';
import { LanguageCode } from '@/types/enum';
import { Locale } from 'antd/es/locale';

export interface BaseJson {
  key: React.Key;
  name?: React.Key;
}

export const DEFAULT_PLACEMENT: React.ReactNode = '--';
export function disableOsDefault(e: React.MouseEvent<any> | KeyboardEvent) {
  if (e) {
    e.stopPropagation();
    e.preventDefault();
  }
}

export const isMobile = () => {
  const flag = navigator.userAgent.match(
    /(phone|pad|pod|iPhone|iPod|ios|iPad|Android|Mobile|BlackBerry|IEMobile|MQQBrowser|JUC|Fennec|wOSBrowser|BrowserNG|WebOS|Symbian|Windows Phone|MiuiBrowser)/i
  );
  return flag;
};

export const routeIsAccess = (path: string, auth: any) => {
  for (let i = 0; i < auth.length; i++) {
    if (auth[i].page_path === path) return auth[i].can_access;
  }
  return false;
};

// 这里需要处理两个问号的情况，因为子系统的链接会在问号后面，但是终止到问号后面第一个&符号，因为子系统的链接会做urlencode处理，读不到&符
// 例如：http://localhost:12139/login?redirect=/app/scrm?scrm=%252Fcustomer-service%252Fstaff%253Factive%2525M2serve-config-list&code=2adn1c5dfada4c80a8831f4aaee0f547&state=
// 实际上后面的code和state参数是要传给/login页面的
export const getHrefParam = (): JsonObject => {
  const location = decodeURIComponent(window.location.href);
  if (location.indexOf('?') < 0) {
    return {};
  }
  const res: JsonObject = {};
  const paramStrArr = location.split('?');
  if (paramStrArr.length === 3) {
    // 说明有两个问号
    const paramStr = paramStrArr[2];
    if (paramStr) {
      paramStr.split('&').forEach((item, index) => {
        if (index === 0) {
          const firstKV = paramStrArr[1].split('=');
          res[firstKV[0]] = `${firstKV[1]}?${item}`;
        } else {
          const [key, value] = item.split('=');
          res[key] = value;
        }
      });
    }
  } else {
    const paramStr = paramStrArr[1];
    if (paramStr) {
      paramStr.split('&').forEach((item) => {
        const [key, value] = item.split('=');
        res[key] = value;
      });
    }
  }
  return res;
};

export type FunctionVariadic = (...args: any[]) => any;

export interface FormMoneyOptions {
  needSeparate: boolean; // 是否开启千分位, 默认开启
  separator: string; // 分割符，默认空格
  exportUnit: 'CN_FEN' | 'CN_JIAO' | 'CN_YUAN'; // 输出数的单位，默认元
  exact: number; // 保留小数位，默认2
}

/**
 * 金额格式化工具方法
 * @param money 金额
 * @param inputUnit 输入的单位
 * @param options 配置项，详见FormMoneyOptions
 * @returns 格式化后的金额 <string>
 */
export const formMoney = (
  money: number,
  inputUnit: 'CN_FEN' | 'CN_JIAO' | 'CN_YUAN',
  options: FormMoneyOptions = {
    needSeparate: true,
    separator: ' ',
    exportUnit: 'CN_YUAN',
    exact: 2,
  }
): string => {
  const divisorMap = {
    CN_FEN: {
      CN_FEN: 1,
      CN_JIAO: 10,
      CN_YUAN: 100,
    },
    CN_JIAO: {
      CN_FEN: 1 / 10,
      CN_JIAO: 1,
      CN_YUAN: 10,
    },
    CN_YUAN: {
      CN_FEN: 1 / 100,
      CN_JIAO: 1 / 10,
      CN_YUAN: 1,
    },
  };
  const divisor = divisorMap[inputUnit][options.exportUnit];
  const result = (money / divisor).toFixed(options.exact);
  if (options.needSeparate) {
    return String(result).replace(/(\d)(?=(\d{3})+\b)/g, `$1${options.separator}`);
  }
  return String(result);
};

interface ChangeMessageProps {
  message: string | React.ReactNode | TFunctionResult;
}

/**
 * message过长时，展开和收起
 * @param {ChangeMessageProps} props
 * @returns {any}
 * @constructor
 */
export function ChangeMessage(props: ChangeMessageProps) {
  const [showAll, setShowAll] = React.useState(false);
  const { message: propMessage = '' } = props;
  let needCut = false;
  if (typeof message === 'string') {
    needCut = (propMessage as string).length > 70;
  }

  return typeof propMessage === 'string' ? (
    <span>
      {needCut ? (
        showAll ? (
          <span>
            {`${propMessage} `}
            <a onClick={() => setShowAll(false)} title={i18n.t('Collapse')}>
              {String(i18n.t('Collapse'))}
            </a>
          </span>
        ) : (
          <span>
            {`${propMessage.slice(0, 70)}... `}
            <a onClick={() => setShowAll(true)} title={i18n.t('Show all')}>
              {String(i18n.t('Show all'))}
            </a>
          </span>
        )
      ) : (
        <span>{propMessage}</span>
      )}
    </span>
  ) : (
    <>{propMessage}</>
  );
}

export function defaultGetErrorMsg(error: AxiosError): string | React.ReactNode | TFunctionResult {
  const err: any = error;
  let detail = false;
  if (err.stackTrace) {
    // 丰富错误显示
    detail = true;
  }
  let code = (_get(error, 'response.data.code') || error.code || '').toString();
  code = code.toLowerCase();
  let errMsg: string | React.ReactNode = _get(error, 'response.data.data.msg', '');
  const status = _get(error, 'response.status');
  if (!errMsg) {
    if (status === 401) {
      errMsg = i18n.t('Token expired');
    } else if (status === 403) {
      errMsg = i18n.t('Permission denied');
    } else if (status === 504 || status === 503) {
      errMsg = i18n.t('Server is down');
    }
  }
  const msg = _get(error, 'message');
  if (!errMsg) {
    errMsg = msg || i18n.t('Api error not defined');
  }
  if (detail) {
    errMsg = (
      <span>
        <>
          {errMsg}
          <span style={{ cursor: 'pointer', textDecoration: 'underline' }}>
            {String(i18n.t('Show detail'))}
          </span>
        </>
      </span>
    );
  }
  return errMsg;
}

export function showError(
  s: string | React.ReactNode | TFunctionResult,
  duration = 4,
) {
  message.destroy();
  message.config({
    top: 8,
    duration,
  });
  message.error(<ChangeMessage message={s} />);
}

export function showMessage(s: string | React.ReactNode, duration = 2) {
  message.destroy();
  message.config({
    top: 8,
    duration,
  });
  message.success(<ChangeMessage message={s} />);
}

export function showInfo(s: string | React.ReactNode, duration = 2) {
  message.config({
    top: 8,
    duration,
  });
  message.info(<ChangeMessage message={s} />);
}

export const languageMap: {[key in LanguageCode]?: {name: string, antd: Locale}} = {
  [LanguageCode.ZhCn]: {
    name: '简体中文',
    antd: zhCN,
  },
  [LanguageCode.ZhTw]: {
    name: '繁體中文',
    antd: zhTW,
  },
  [LanguageCode.En]: {
    name: 'English',
    antd: enUS,
  },
  [LanguageCode.Ko]: {
    name: '한국어',
    antd: koKR,
  },
  [LanguageCode.In]: {
    name: 'bahasa Indonesia',
    antd: idIN,
  },
  [LanguageCode.Th]: {
    name: 'ภาษาไทย',
    antd: thTH,
  },
  [LanguageCode.Pt]: {
    name: 'Português',
    antd: ptPT,
  },
  [LanguageCode.Es]: {
    name: 'Español',
    antd: esES,
  },
};

export const deepClone = (obj: any) =>
  new Promise((resolve) => {
    const { port1, port2 } = new MessageChannel();
    port2.onmessage = (v) => resolve(v.data);
    port1.postMessage(obj);
  });

export const getXUa = () => {
  return `OS=${navigator.platform}&LANG=${
    localStorage.getItem('i18nextLng') || 'en'
  }&PL=Web&APP_CODE=joy_dev_platform&PROJECT_CODE=default&V=${PackageJson.version}&ENV=${
    import.meta.env.VITE_APP_ENV
  }`;
};

export const isUrlEncoded = (str: string) => {
  try {
    return encodeURIComponent(decodeURIComponent(str)) === str;
  } catch (e) {
    return true;
  }
};

export const trimObjectValues = <T extends Record<string, string>>(obj: T): T => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, val]) => [key, typeof val === 'string' ? _.trim(val) : val])
  ) as T;
};
/**
 * 检查给定的字符串是否不包含特定字符。
 *
 * @param inputString - 原始字符。
 * @param charToCheck - 需要检查的字符。该字符中的空格会被适当处理。
 * @returns 如果字符串不包含该字符，则返回 true；否则返回 false。
 *          字符串中的空格不会被处理。
 *          比较时不区分大小写。
 *          如果输入无效，则抛出错误。
 */
export function doesNotContainChar(inputString: string, charToCheck: string): boolean {
  // 如果 charToCheck 不存在，则直接返回 true
  if (!charToCheck) {
    return true;
  }
  if (!inputString) {
    // 如果 inputString 不存在，则直接返回 false
    return false;
  }
  // 验证输入有效性
  if (typeof inputString !== 'string' || typeof charToCheck !== 'string') {
    throw new Error('Both inputString and charToCheck must be strings.');
  }
  if (charToCheck.length === 0) {
    throw new Error('charToCheck cannot be an empty string.');
  }

  // 处理待检查字符：去除首尾空格
  const cleanedCharToCheck = charToCheck.trim();

  // 将输入字符串和待检查字符都转换为小写
  const lowerCaseInput = inputString.toLowerCase();
  const lowerCaseChar = cleanedCharToCheck.toLowerCase();

  // 检查清理后的字符串是否包含该字符
  return lowerCaseInput.includes(lowerCaseChar);
}

/**
 * 根据提供的字符串过滤树形结构中的节点，不区分大小写。
 * 如果参数错误，则返回全部数据。
 * 当当前节点满足条件时，直接返回当前节点，子节点不再参与过滤。
 *
 * @param {string} searchString - 需要搜索的字符串。
 * @param {string} fieldName - 用于搜索的字段名。
 * @param {Array<Object>} treeData - 树形结构数据。
 * @param {Array<Object>} checkedKeys - 选中的节点。
 * @returns {Array<Object>} - 过滤后的树形结构数据或原始数据（如果参数错误）。
 */
export function filterTreeByString(searchString, fieldName, treeData, checkedKeys: any[]) {
  // 参数验证
  if (!searchString || !fieldName || !treeData || !Array.isArray(treeData)) {
    return treeData;
  }

  const lowerCaseSearchString = searchString.trim().toLowerCase();

  return treeData.filter((node) => {
    // 检查当前节点的指定字段是否包含 searchString（不区分大小写）
    const matchesField = node[fieldName]
      ? node[fieldName].toLowerCase().includes(lowerCaseSearchString)
      : false;

    const matchsKeys = checkedKeys?.includes(node.resource_key || node.id);

    // 如果当前节点满足条件，直接返回当前节点
    if (matchesField || matchsKeys) {
      return true;
    }

    // 如果当前节点有子节点，则递归过滤子节点
    if (node.children && node.children.length > 0) {
      node.children = filterTreeByString(searchString, fieldName, node.children, checkedKeys);
    }

    // 返回当前节点或其子节点中匹配的节点
    return node.children && node.children.length > 0;
  });
}

export const judgeSets = (str) => {
  const [, names] = str?.split('.');
  if (!names) return false;
  // 判断字符串是否以 "pf_" 开头
  const startsWithPF = names.startsWith('pf_');

  // 判断字符串是否以 "_view" 结尾
  const endsWithView = names.endsWith('_view');

  // 如果同时满足两个条件，则返回 true
  return startsWithPF && endsWithView;
};

export const formatURLParams = (params: Record<string, any> = {}, projectCode?: string) => {
  const res = Object.keys(params)
  .map((key) => {
    return `${key}=${params[key]}`;
  })
  if(projectCode) {
    res.push(`project_code=${projectCode}`)
  }
  return res.join('&');
};

// 获取链接参数的值
export const getUrlParamValue = (paramName: string): string | null => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  return urlSearchParams.get(paramName) || null;
};

export const commonSelectFilterFunc: (
  input: string,
  option: any,
  searchKey: string[],
) => boolean = (input, option, searchKey) => {
  const searchValue = input.trim()
  let flag = false;
  if (searchKey?.length) {
    if (Array.isArray(option)) {
      flag = option.some(optionItem => commonSelectFilterFunc(searchValue, optionItem, searchKey));
    } else {
      flag = searchKey.some(key =>
        `${option?.[key]}`?.toLowerCase()?.includes(searchValue?.toLowerCase()),
      );
    }
  } else {
    flag = `${option?.label}`?.toLowerCase()?.includes(searchValue?.toLowerCase());
  }
  return flag;
};
